/* eslint-disable no-unused-vars */
/**
 * == CALCULATION RULES ==
 *
 * Run `npm run test` to test this file
 * Please keep these examples up-to-date if they change
 *
 * Example US play
 *  Title Minimum Fee Per Performance: 125
 *  Amateur Title Percentage: 7
 *  Seats: 100
 *  Low Ticket: 1; High Ticket: 5; Average: 3
 *  Number of Performances: 4
 *  Calculation: 100 x 3 x 4 x .07 = 84
 *  Result: 125
 *  In this example, the title minimum fee per performance would display instead of the calculated fee.
 *
 * Example UK play
 *  Title Minimum Fee Per Performance: 125
 *  Amateur Title Percentage: 16
 *  Seats: 400
 *  Low Ticket: 5; High Ticket: 10; Average: 7.5
 *  Number of Performances: 4
 *  Calculation: 400 x 7.5 x .16 x 4
 *  Result: 1920
 */

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isInteger#Polyfill
if (!Number.MAX_SAFE_INTEGER) {
    Number.MAX_SAFE_INTEGER = 9007199254740991; // Math.pow(2, 53) - 1;
}

Number.isSafeInteger = Number.isSafeInteger || function (value) {
   return Number.isInteger(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER;
};

/**
 * The fee constants
 * Gets replaced in the curried function if required
 * !!KEEP IN SYNC WITH `config/estimator.php`!!
 *
 * @type {Object}
 */
const fees = {
    ZERO_TICKET_PRICE_MULTIPLIER: {
        us: 0.5,
        uk: 0,
    },
    DEFAULT_SHIPPING_FEE: {
        us: 12.95,
        uk: 7.95,
    },
    SUBTOTAL_ROUNDING: 5,
    DEFAULT_RATE_MULTIPLIER: 100,
    UK_TICKET_MINIMUM: 7,
    UK_TICKET_MULTIPLIER: 0.02,
    UK_SEAT_INCREMENT_THRESHOLD: 100,
    UK_SEAT_UPPER_LIMIT: 500,
    UK_SEAT_SUBTRACTION: 400,
    UK_SEAT_MULTIPLIER: 4,
};

/**
 * Round the total up without subtotals based on the rounding rules
 *
 * @param {number} price
 * @param {number} rounding
 *
 * @return {number}
 */
export function roundUpWithRounding(price, rounding) {
    if (price === 0 || Number.isSafeInteger(price)) {
        return price;
    }

    const theRounding = rounding || fees.SUBTOTAL_ROUNDING;

    return Math.ceil(price / theRounding) * theRounding;
}

/**
 * Round the total up based on the rounding rules
 *
 * @param {number} price
 *
 * @return {number}
 */
export function roundUp(price) {
    return roundUpWithRounding(price, fees.SUBTOTAL_ROUNDING);
}

/**
 * Get the average ticket price given the low and high prices
 *
 * @param {Object} options
 * @param {number} options.low
 * @param {number} options.high
 *
 * @return {number}
 */
function getAverageTicketPrice({ low, high }) {
    const avg = (high + low) / 2;

    return avg === 0 ? 0 : avg;
}

/**
 * Calculate the fee for the US plays and musicals
 *
 * @param {Object} options
 * @param {string} options.licensingClass the type of Title this is
 * @param {number} options.seats the number of seats in the venue
 * @param {Object} options.tickets the low and high ticket values
 * @param {number} options.tickets.low
 * @param {number} options.tickets.high
 * @param {number} options.performances the number of performances
 * @param {number} options.rateMultiplier the amateur title percentage multiplier
 * @param {number} options.minimumFee the minimum fee to return
 *
 * @return {number}
 */
function calculateUSfee({ licensingClass, seats, tickets, performances, rateMultiplier, minimumFee }) {
    const averageTicketPrice = getAverageTicketPrice(tickets) || fees.ZERO_TICKET_PRICE_MULTIPLIER.us;

    // the licensing platform is rounding up to the nearest SUBTOTAL_ROUNDING per performance
    const subTotal = roundUp(seats * averageTicketPrice * rateMultiplier) * performances;

    const roundedTotal = licensingClass.indexOf('musical') !== -1 ? roundUp(subTotal) : subTotal;

    return roundedTotal > minimumFee ? roundedTotal : minimumFee;
}

/**
 * Calculate the seating increment for the given Title
 *
 * @param {number} base the base licence fee
 * @param {number} seats the number of seats
 * @param {Object} tickets ticket low price
 * @param {number} tickets.low ticket low price
 * @param {number} tickets.high ticket high price
 *
 * @return {number}
 */
function getSeatingIncrement(base, seats, tickets) {
    throw new Error('TODO: bring back seat increment logic when UK SF team figures out what to charge here...');

    // if (seats < fees.UK_SEAT_INCREMENT_THRESHOLD) {
    //     return base;
    // }

    // const { low, high } = tickets;

    // if (seats >= fees.UK_SEAT_UPPER_LIMIT) {
    //     const seatFee = Math.floor((seats - fees.UK_SEAT_SUBTRACTION) / fees.UK_SEAT_INCREMENT_THRESHOLD);

    //     const additional = Math.floor(seatFee * high) + (low * fees.UK_SEAT_MULTIPLIER);

    //     return base + additional;
    // }

    // const increment = Math.floor(seats / fees.UK_SEAT_INCREMENT_THRESHOLD) * low;

    // return Math.round(base + increment);
}

/**
 * Calculate the UK ticket increments
 *
 * @param {number} increment the seating increment fee
 * @param {number} price the average ticket price
 *
 * @return {number}
 */
function getTicketIncrement(increment, price) {
    throw new Error('TODO: bring back seat increment logic when UK SF team figures out what to charge here...');

    // if (price < (fees.UK_TICKET_MINIMUM + 1)) {
    //     return increment;
    // }

    // const multiplier = price > fees.UK_TICKET_MINIMUM ? price - (fees.UK_TICKET_MINIMUM * -1) : 1;

    // const ticketIncrement = multiplier * fees.UK_TICKET_MULTIPLIER;

    // return Math.round((ticketIncrement * increment) + increment);
}

/**
 * Calculate the fee for the UK plays and musicals
 *
 * @param {Object} options
 * @param {string} options.licensingClass the type of Title this is
 * @param {number} options.seats the number of seats in the venue
 * @param {Object} options.tickets the low and high ticket values
 * @param {number} options.tickets.low
 * @param {number} options.tickets.high
 * @param {number} options.performances the number of performances
 * @param {number} options.rateMultiplier the amateur title percentage multiplier
 * @param {number} options.minimumFee the minimum fee to return
 *
 * @return {number}
 */
function calculateUKfee({ licensingClass, seats, tickets, performances, rateMultiplier, minimumFee }) {
    const averageTicketPrice = getAverageTicketPrice(tickets) || fees.ZERO_TICKET_PRICE_MULTIPLIER.uk;

    const subTotal = (seats * averageTicketPrice * rateMultiplier) * performances;

    const roundedTotal = licensingClass.indexOf('musical') !== -1 ? roundUp(subTotal) : subTotal;

    return roundedTotal > minimumFee ? roundedTotal : minimumFee;

    // TODO: bring back seat increment logic when UK SF team figures out what to charge here...
    // const seatingIncrement = getSeatingIncrement(minimumFee, seats, tickets);
    // const ticketIncrement = getTicketIncrement(seatingIncrement, averageTicketPrice);

    // return Math.round(ticketIncrement * performances);
}

/**
 * Calculate the licence fees based on the fee structure and Title
 *
 * @param {Object} feeBreakdown
 *
 * @return {Function}
 */
function calculate(feeBreakdown) {
    // replace the constants if required
    Object.entries(feeBreakdown).forEach(([key, value]) => {
        fees[key] = value;
    });

    /**
     * Calculate the fee for the given Title
     *
     * @param {Object} options
     * @param {string} options.licensingClass the type of Title this is
     * @param {number} options.seats the number of seats in the venue
     * @param {Object} options.tickets the low and high ticket values
     * @param {number} options.tickets.low
     * @param {number} options.tickets.high
     * @param {number} options.performances the number of performances
     * @param {number} options.rateMultiplier the amateur title percentage multiplier
     * @param {number} options.minimumFee the minimum fee to return
     *
     * @return {number}
     */
    return (options) => {
        // make sure we dont multiply the results by `0`
        options.rateMultiplier = (options.rateMultiplier === 0 ? fees.DEFAULT_RATE_MULTIPLIER : options.rateMultiplier) / 100;

        return options.location.toLowerCase() === 'us' ? calculateUSfee(options) : calculateUKfee(options);
    }
}

/**
 * Calculate the licence for the given Title
 *
 * @type {Function}
 */
// module.exports = calculate; // swap exports when testing in node
export default calculate;



// WEBPACK FOOTER //
// ./resources/assets/js/licence-calculations.js